import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarClock, Users, MapPin, Star, CheckCircle2, XCircle, HelpCircle, Clock } from "lucide-react";
import PendingBookingBanner from "@/components/PendingBookingBanner";
import { formatTime } from "@/lib/time";

const RSVP_POLL_MS = 15000;

export default function Dashboard() {
  const [dinners, setDinners] = useState([]);
  const [members, setMembers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [upcoming, setUpcoming] = useState(null); // {dinner, restaurant, rsvp_counts, total, polled_at}

  useEffect(() => {
    Promise.all([
      api.get("/dinners").then(r => r.data),
      api.get("/members").then(r => r.data),
      api.get("/restaurants").then(r => r.data),
    ]).then(([d, m, r]) => { setDinners(d); setMembers(m); setRestaurants(r); });
  }, []);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/dashboard/upcoming").then(r => { if (alive) setUpcoming(r.data); }).catch(()=>{});
    load();
    const t = setInterval(load, RSVP_POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const restNameById = Object.fromEntries(restaurants.map(r => [r.id, r.name]));
  const recent = dinners.slice(0, 5);
  const totalHouseholds = new Set(members.filter(m=>m.active).map(m => m.household_id)).size;

  return (
    <div data-testid="dashboard-hero-section" className="space-y-10">
      <PendingBookingBanner/>
      <section className="relative overflow-hidden rounded-2xl bg-secondary p-8 sm:p-12">
        <div className="max-w-2xl">
          <div className="overline mb-3">Tonight</div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
            Find a dinner that works for everyone.
          </h1>
          <p className="text-muted-foreground mb-6 leading-relaxed">
            Availability, travel times, preferences, and history — reconciled into one recommendation with two alternatives. You approve.
          </p>
          <Link to="/plan"><Button data-testid="plan-dinner-primary-cta" size="lg" className="rounded-full px-8">Plan a dinner</Button></Link>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card data-testid="upcoming-dinner-card" className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 overline"><CalendarClock className="w-4 h-4"/>Next dinner</div>
            {upcoming?.polled_at && <div className="text-[10px] mono text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3"/>live · updated {new Date(upcoming.polled_at).toLocaleTimeString()}</div>}
          </div>
          {upcoming?.dinner ? (
            <Link to={`/dinners/${upcoming.dinner.id}`} className="block group">
              <div className="font-serif text-2xl mb-1">{upcoming.restaurant?.name || restNameById[upcoming.dinner.restaurant_id] || "Restaurant TBD"}</div>
              <div className="text-sm text-muted-foreground mono">{upcoming.dinner.dinner_date} · {formatTime(upcoming.dinner.approved_time || upcoming.dinner.recommended_time)}</div>
              {upcoming.rsvp_counts && upcoming.total > 0 && (
                <div className="mt-4 grid grid-cols-4 gap-2" data-testid="rsvp-counter">
                  <RsvpCounter icon={CheckCircle2} label="In" count={upcoming.rsvp_counts.yes} tone="emerald"/>
                  <RsvpCounter icon={HelpCircle} label="Maybe" count={upcoming.rsvp_counts.maybe} tone="amber"/>
                  <RsvpCounter icon={XCircle} label="Out" count={upcoming.rsvp_counts.no} tone="red"/>
                  <RsvpCounter icon={CalendarClock} label="No reply" count={upcoming.rsvp_counts.no_reply} tone="muted"/>
                </div>
              )}
              <div className="mt-4 inline-block text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200" data-testid="upcoming-dinner-status-badge">
                {upcoming.dinner.status.replaceAll("_"," ")}
              </div>
            </Link>
          ) : (
            <div className="text-muted-foreground text-sm">No upcoming dinner. Plan one.</div>
          )}
        </Card>

        <Card className="p-6">
          <div className="overline mb-3">The group</div>
          <div className="flex items-center gap-2 text-sm mb-2"><Users className="w-4 h-4 text-primary"/> {members.filter(m=>m.active).length} active members</div>
          <div className="flex items-center gap-2 text-sm mb-2"><MapPin className="w-4 h-4 text-primary"/> {totalHouseholds} households</div>
          <div className="flex items-center gap-2 text-sm"><Star className="w-4 h-4 text-primary"/> {restaurants.filter(r=>r.active).length} restaurants</div>
          <Link to="/group" className="text-sm text-primary mt-4 inline-block">Manage group →</Link>
        </Card>
      </div>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">Recent dinners</h2>
          <Link to="/history" className="text-sm text-muted-foreground">See all</Link>
        </div>
        <div data-testid="recent-dinners-list" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recent.length === 0 && <div className="text-muted-foreground text-sm">No dinners yet.</div>}
          {recent.map(d => (
            <Link key={d.id} to={`/dinners/${d.id}`}>
              <Card className="p-5 hover:border-primary transition">
                <div className="font-serif text-lg">{restNameById[d.restaurant_id] || "Unassigned"}</div>
                <div className="text-xs mono text-muted-foreground mt-1">{d.dinner_date} · {formatTime(d.approved_time || d.recommended_time)}</div>
                <div className="text-xs mt-2 text-muted-foreground">{d.participant_member_ids?.length || 0} guests · {d.status.replaceAll("_"," ")}</div>
              </Card>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function RsvpCounter({ icon: Icon, label, count, tone }) {
  const tones = {
    emerald: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300",
    amber: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300",
    red: "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300",
    muted: "bg-secondary text-muted-foreground",
  };
  return (
    <div className={`rounded-lg p-3 ${tones[tone]}`} data-testid={`rsvp-counter-${label.toLowerCase().replace(" ","-")}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest"><Icon className="w-3 h-3"/>{label}</div>
      <div className="font-serif text-2xl mono">{count}</div>
    </div>
  );
}
